import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack } from '@mui/material';
import { SideNavItem } from './side-nav-item';

interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string | { index: string; details: string; edit: string };
  title: string;
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
  isCollapsed
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
  isCollapsed?: boolean;
}): JSX.Element[] => items.reduce(
  (acc: JSX.Element[], item) => reduceChildRoutes({
    acc,
    depth,
    item,
    pathname,
    isCollapsed
  }),
  []
);

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  isCollapsed
}: {
  acc: JSX.Element[];
  depth: number;
  item: Item;
  pathname?: string | null;
  isCollapsed?: boolean;
}): Array<JSX.Element> => {
  const checkPath = !!(item.path && pathname);
  const pathToCheck = item.path ? (typeof item.path === 'string' ? item.path : item.path.index) : '';
  const partialMatch = checkPath ? pathname.includes(pathToCheck) : false;
  const exactMatch = checkPath ? pathname === pathToCheck : false;

  if (item.items) {
    acc.push(
      <SideNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={isCollapsed ? undefined : item.label}
        open={partialMatch}
        title={item.title}
        isCollapsed={isCollapsed}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            isCollapsed
          })}
        </Stack>
      </SideNavItem>
    );
  } else {
    acc.push(
      <SideNavItem
        active={exactMatch}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={isCollapsed ? undefined : item.label}
        path={item.path}
        title={item.title}
        isCollapsed={isCollapsed}
      />
    );
  }

  return acc;
};

interface SideNavSectionProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
  isCollapsed?: boolean;
}

export const SideNavSection: FC<SideNavSectionProps> = (props) => {
  const { items = [], pathname, subheader = '', isCollapsed = false, ...other } = props;

  return (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0
      }}
      {...other}
    >
      {subheader && !isCollapsed && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: 'uppercase'
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items, pathname, isCollapsed })}
    </Stack>
  );
};

SideNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
  isCollapsed: PropTypes.bool
};
